import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { editCardDetails } from 'actions/cardActions';
import { trackCustomEvent } from 'utilities/app-insights';
import { LoggedInUser } from 'types/UserState';
import { useEvAcknowledgeModal } from './use-ev-acknowledge-modal';
import Env from 'Env';

type UseReissueFlowOptions = {
  assignedGrandfatherClientNumber: string;
  loggedInUser: LoggedInUser;
  assignedAccountNumber: string;
  onClose: () => void;
  onEvReissueComplete?: () => void;
};

export const useReissueFlow = ({
  assignedGrandfatherClientNumber,
  loggedInUser,
  assignedAccountNumber,
  onClose,
  onEvReissueComplete,
}: UseReissueFlowOptions) => {
  const dispatch = useDispatch();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  // When the user clicks Confirm, the design system also calls onClose (= cancelReissue)
  // as a side effect of closing the modal. This ref lets cancelReissue ignore that
  // extra call so it doesn't undo what confirmReissue just set up.
  const reissueConfirmedRef = useRef(false);
  const [showReissueModal, setShowReissueModal] = useState(false);
  const [reissueRequest, setReissueRequest] = useState<any>({});
  const [isEvReissue, setIsEvReissue] = useState(false);
  const { showEvAcknowledgeModal, setShowEvAcknowledgeModal, triggerEvAcknowledge } =
    useEvAcknowledgeModal(onEvReissueComplete);

  const initiateReissue = (request: any, isEv = false) => {
    setReissueRequest(request);
    setIsEvReissue(isEv);
    setShowReissueModal(true);
  };

  const confirmReissue = () => {
    dispatch(editCardDetails(reissueRequest, assignedGrandfatherClientNumber));
    trackCustomEvent(
      'Submit Edit Card',
      loggedInUser,
      assignedAccountNumber,
      'Click Submit to submit an Edit Card that causes a card reissue.',
      { selectedAccount: reissueRequest?.contractNumber, selectedCard: reissueRequest?.contractIdentifier }
    );
    reissueConfirmedRef.current = true;
    setShowReissueModal(false);
    if (isEvReissue && isEvWay4Enabled) {
      triggerEvAcknowledge();
    } else {
      onClose();
    }
  };

  const acknowledgeEvReissue = () => {
    setShowEvAcknowledgeModal(false);
    onClose();
  };

  const cancelReissue = () => {
    // The design system calls onClose (this function) whenever it closes the modal,
    // even when the user clicked Confirm. Skip the cancel logic in that case.
    if (reissueConfirmedRef.current) {
      reissueConfirmedRef.current = false;
      return;
    }
    setShowReissueModal(false);
    setIsEvReissue(false);
  };

  return {
    showReissueModal,
    reissueRequest,
    showEvAcknowledgeModal,
    initiateReissue,
    confirmReissue,
    acknowledgeEvReissue,
    cancelReissue,
  };
};
